import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, ButtonVariants, BadgeVariants } from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeSelection } from '@/hooks';
import { ConfirmDialog } from '@/components/feedback';
import { ROUTES } from '@/app/routes';
import {
  DiscrepancyModal,
  SurplusWriteoffModal,
  SURPLUS_WRITEOFF_MODES,
} from '@/components/common';
import { useUpdateSurplusWriteoffMutation } from '@/lib/api/surplusWriteoffApi';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { buildLinkedDeclarationsColumns } from '../constants/incomingShipmentLinkedDeclarationsColumns';
import { useGetLinkedDeclarationsForShipmentQuery } from '../api/incomingShipmentsApi';

const getLinkedDeclarationId = (row) => row.id;

/**
 * Props:
 *   module             the active module route segment
 *   shipmentId         the shipment's database id
 *   shipment           the shipment detail view model
 *   onAddDeclarations  () => void — opens the add-declaration flow
 */
export const LinkedDeclarationsTabView = ({ module, shipmentId, shipment, onAddDeclarations }) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { data: linkedDeclarations } = useGetLinkedDeclarationsForShipmentQuery({
    id: shipmentId,
  });
  const [removeLinkageRow, setRemoveLinkageRow] = useState(null);
  const [declarationDiscrepancyRow, setDeclarationDiscrepancyRow] = useState(null);
  const [surplusWriteoffRow, setSurplusWriteoffRow] = useState(null);
  const [updateSurplusWriteoff, { isLoading: isSavingSurplusWriteoff }] =
    useUpdateSurplusWriteoffMutation();

  const { selections: unitSelections, contextValue: unitTypeContext } = useUnitTypeSelection();

  // DRAFT still needs validating; every other status opens the read/edit view.
  const handleViewDeclaration = useCallback(
    (row) => {
      const route =
        row.statusValue === 'DRAFT'
          ? ROUTES.validateDeclaration(module, row.id)
          : ROUTES.declarationEdit(module, row.id);
      navigate(route, { state: { from: ROUTES.incomingShipmentDetail(module, shipmentId) } });
    },
    [navigate, module, shipmentId],
  );
  const handleFulfillDeclaration = useCallback(() => {
    // TODO: open the fulfill-declaration flow once it exists.
  }, []);
  const handleSurplusWriteOff = useCallback((row) => setSurplusWriteoffRow(row), []);
  const handleCloseSurplusWriteoff = useCallback(() => setSurplusWriteoffRow(null), []);
  const handleSubmitSurplusWriteoff = useCallback(
    async (values) => {
      try {
        await updateSurplusWriteoff({
          module,
          id: surplusWriteoffRow.id,
          shipmentId,
          ...values,
        }).unwrap();
        setSurplusWriteoffRow(null);
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [updateSurplusWriteoff, module, shipmentId, surplusWriteoffRow],
  );
  const handleRemoveLinkage = useCallback((row) => setRemoveLinkageRow(row), []);
  const handleCancelRemoveLinkage = useCallback(() => setRemoveLinkageRow(null), []);
  // TODO: replace with a real remove-linkage mutation once the backend exposes one.
  const handleConfirmRemoveLinkage = useCallback(() => setRemoveLinkageRow(null), []);
  const handleOpenDeclarationDiscrepancy = useCallback(
    (row) => setDeclarationDiscrepancyRow(row),
    [],
  );
  const handleCloseDeclarationDiscrepancy = useCallback(
    () => setDeclarationDiscrepancyRow(null),
    [],
  );

  const columns = useMemo(
    () =>
      buildLinkedDeclarationsColumns(t, {
        onView: handleViewDeclaration,
        onOpenDiscrepancy: handleOpenDeclarationDiscrepancy,
      }),
    [t, handleViewDeclaration, handleOpenDeclarationDiscrepancy],
  );

  const rowActions = useMemo(
    () => [
      {
        id: 'remove-linkage',
        label: t('ACTION_REMOVE_LINKAGE'),
        variant: ButtonVariants.tertiary,
        onClick: handleRemoveLinkage,
      },
      {
        id: 'fulfill',
        label: t('ACTION_FULFILL'),
        variant: ButtonVariants.tertiary,
        onClick: handleFulfillDeclaration,
      },
      {
        id: 'add-surplus-write-off',
        label: t('DECLARATIONS_ACTION_ADD_SURPLUS_WRITE_OFF'),
        variant: ButtonVariants.tertiary,
        onClick: handleSurplusWriteOff,
        hidden: (row) => row.hasSurplusOrWriteOff,
      },
      {
        id: 'edit-surplus-write-off',
        label: t('DECLARATIONS_ACTION_EDIT_SURPLUS_WRITE_OFF'),
        variant: ButtonVariants.tertiary,
        onClick: handleSurplusWriteOff,
        hidden: (row) => !row.hasSurplusOrWriteOff,
      },
    ],
    [t, handleRemoveLinkage, handleFulfillDeclaration, handleSurplusWriteOff],
  );

  const declarationDiscrepancies = declarationDiscrepancyRow?.discrepancyItems ?? [];

  const declarationDiscrepancySections = useMemo(() => {
    if (!declarationDiscrepancyRow) return [];
    const selectedUnit =
      unitSelections.get(declarationDiscrepancyRow.id) ?? declarationDiscrepancyRow.unitType;
    const unitValues = declarationDiscrepancyRow.byUnit?.[selectedUnit] ?? {};
    return [
      {
        title: t('SECTION_GENERAL_DETAILS'),
        fields: [
          {
            label: t('DECLARATIONS_COL_REFERENCE_ID'),
            value: declarationDiscrepancyRow.referenceId,
          },
          {
            label: t('FIELD_DECLARATION_NO'),
            value: declarationDiscrepancyRow.declarationNo,
          },
          {
            label: t('FIELD_STATUS'),
            value: declarationDiscrepancyRow.status,
            type: 'status',
            tone: BadgeVariants.success,
          },
        ],
      },
      {
        title: t('SECTION_QUANTITY'),
        fields: [
          {
            label: t('FIELD_ASSIGNED_QUANTITY_SHIPMENT'),
            value: unitValues.assignedQty,
          },
          { label: t('FIELD_RECEIVED_DECLARATION_QUANTITY'), value: unitValues.receivedQty },
          { label: t('FIELD_AVAILABLE_QUANTITY'), value: unitValues.availableQty },
        ],
      },
    ];
  }, [declarationDiscrepancyRow, unitSelections, t]);

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('INCOMING_SHIPMENTS_DETAIL_LINKED_DECLARATIONS_EMPTY_TITLE'),
      description: t('INCOMING_SHIPMENTS_DETAIL_LINKED_DECLARATIONS_EMPTY_DESCRIPTION'),
      primaryLabel: t('ACTION_ADD_DECLARATIONS'),
      onPrimaryClick: onAddDeclarations,
    }),
    [t, onAddDeclarations],
  );

  const surplusWriteoffMode = surplusWriteoffRow?.hasSurplusOrWriteOff
    ? SURPLUS_WRITEOFF_MODES.edit
    : SURPLUS_WRITEOFF_MODES.add;

  return (
    <>
      <UnitTypeContext.Provider value={unitTypeContext}>
        <Table
          columns={columns}
          data={linkedDeclarations ?? []}
          getRowId={getLinkedDeclarationId}
          searchable
          rowActions={rowActions}
          actionsHeader={t('FIELD_ACTION')}
          emptyState={emptyState}
        />
      </UnitTypeContext.Provider>

      <DiscrepancyModal
        open={declarationDiscrepancyRow != null}
        onClose={handleCloseDeclarationDiscrepancy}
        referenceId={declarationDiscrepancyRow?.referenceId}
        discrepancies={declarationDiscrepancies}
        sections={declarationDiscrepancySections}
      />

      <ConfirmDialog
        open={removeLinkageRow != null}
        title={t('ACTION_REMOVE_LINKAGE')}
        bodyTitle={t('INCOMING_SHIPMENTS_LINKED_DECLARATIONS_REMOVE_LINKAGE_BODY_TITLE', {
          referenceId: removeLinkageRow?.referenceId,
          shipmentId: shipment.shipmentId,
        })}
        description={t('REMOVE_LINKAGE_DESCRIPTION')}
        confirmLabel={t('COMMON_REMOVE')}
        tone="danger"
        onConfirm={handleConfirmRemoveLinkage}
        onCancel={handleCancelRemoveLinkage}
      />

      <SurplusWriteoffModal
        open={surplusWriteoffRow != null}
        onClose={handleCloseSurplusWriteoff}
        mode={surplusWriteoffMode}
        row={surplusWriteoffRow}
        onSubmit={handleSubmitSurplusWriteoff}
        isSubmitting={isSavingSurplusWriteoff}
      />
    </>
  );
};
